import React, { useState } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  Input,
  Image,
  IconButton,
  SimpleGrid,
  useToast,
} from "@chakra-ui/react";
import { MdDelete, MdAdd, MdPlayCircle } from "react-icons/md";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import MediaLightbox from "./MediaLightbox";

const NEWS_PAGE_ID = PAGE_IDS.NEWS;

// Adds/removes individual images and videos on an existing news item via the
// separate single-file endpoints (POST/DELETE /newsimage, /newsvideo) — kept
// apart from the multi-file upload done at creation time in CreateNews.js.
function NewsMediaManager({ newsId, images, videos, onChange }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null);

  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("page_id", NEWS_PAGE_ID);
      formData.append("news_id", newsId);
      formData.append("Image", file);
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsimage`,
        "POST",
        formData
      );
      if (!response) throw new Error("Failed to upload image");
      onChange?.();
      toast({ title: t("Image added."), status: "success", duration: 3000, isClosable: true, position: "top-right" });
    } catch (error) {
      toast({ title: t("Failed to add image."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append("page_id", NEWS_PAGE_ID);
      formData.append("news_id", newsId);
      formData.append("Video", file);
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsvideo`,
        "POST",
        formData
      );
      if (!response) throw new Error("Failed to upload video");
      onChange?.();
      toast({ title: t("Video added."), status: "success", duration: 3000, isClosable: true, position: "top-right" });
    } catch (error) {
      toast({ title: t("Failed to add video."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsimage`,
        "DELETE",
        { page_id: NEWS_PAGE_ID, image_id: imageId }
      );
      if (!response) throw new Error("Failed to delete image");
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to delete image."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  const handleDeleteVideo = async (videoId) => {
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/newsvideo`,
        "DELETE",
        { page_id: NEWS_PAGE_ID, video_id: videoId }
      );
      if (!response) throw new Error("Failed to delete video");
      onChange?.();
    } catch (error) {
      toast({ title: t("Failed to delete video."), description: error.message, status: "error", duration: 5000, isClosable: true, position: "top-right" });
    }
  };

  return (
    <Box mt="6">
      <Flex justify="space-between" align="center" mb="2">
        <Text fontSize="md" fontWeight="bold">{t("Images")}</Text>
        <Button
          as="label"
          size="xs"
          leftIcon={<MdAdd />}
          variant="outline"
          borderColor={GOLD_BORDER}
          color={GOLD_TEXT}
          isLoading={uploadingImage}
          cursor="pointer"
        >
          {t("Add Image")}
          <Input type="file" accept="image/*" display="none" onChange={handleAddImage} />
        </Button>
      </Flex>
      {(!images || images.length === 0) ? (
        <Text fontSize="sm" color="gray.500" mb="4">{t("No images yet.")}</Text>
      ) : (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb="4">
          {images.map((img) => (
            <Box key={img.id} position="relative" borderRadius="md" overflow="hidden" borderWidth="1px" borderColor="gray.200">
              <Image
                src={img.image_url}
                alt={img.caption || "news image"}
                objectFit="cover"
                h="90px"
                w="100%"
                cursor="pointer"
                _hover={{ opacity: 0.85 }}
                onClick={() => setLightboxMedia({ type: "image", src: img.image_url, alt: img.caption })}
              />
              <IconButton
                aria-label="Remove image"
                icon={<MdDelete />}
                size="xs"
                colorScheme="red"
                position="absolute"
                top="2px"
                right="2px"
                onClick={() => handleDeleteImage(img.id)}
              />
            </Box>
          ))}
        </SimpleGrid>
      )}

      <Flex justify="space-between" align="center" mb="2">
        <Text fontSize="md" fontWeight="bold">{t("Videos")}</Text>
        <Button
          as="label"
          size="xs"
          leftIcon={<MdAdd />}
          variant="outline"
          borderColor={GOLD_BORDER}
          color={GOLD_TEXT}
          isLoading={uploadingVideo}
          cursor="pointer"
        >
          {t("Add Video")}
          <Input type="file" accept="video/*" display="none" onChange={handleAddVideo} />
        </Button>
      </Flex>
      {(!videos || videos.length === 0) ? (
        <Text fontSize="sm" color="gray.500">{t("No videos yet.")}</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          {videos.map((vid) => (
            <Flex key={vid.id} align="center" justify="space-between" borderWidth="1px" borderColor="gray.200" borderRadius="md" p="2">
              <Flex
                align="center"
                gap={2}
                cursor="pointer"
                onClick={() => setLightboxMedia({ type: "video", src: vid.video_url, alt: vid.caption })}
                _hover={{ color: GOLD_TEXT }}
              >
                <MdPlayCircle />
                <Text fontSize="sm" noOfLines={1} maxW="180px">
                  {vid.caption || t("Video")}
                </Text>
              </Flex>
              <IconButton
                aria-label="Remove video"
                icon={<MdDelete />}
                size="xs"
                colorScheme="red"
                onClick={() => handleDeleteVideo(vid.id)}
              />
            </Flex>
          ))}
        </SimpleGrid>
      )}

      <MediaLightbox media={lightboxMedia} onClose={() => setLightboxMedia(null)} />
    </Box>
  );
}

export default NewsMediaManager;
